import { useEffect, useState } from 'react';
import { t } from 'i18next';
import * as jose from 'jose';
import store from '../store/store';
import { getAppConfig } from './getConfig';
import { verifyLicense, getLicenseConfig } from './licenseUtils';
import { addConfig } from '../actions/ConfigActions';
import { initSentry } from './initSentry';
import { getChildViewportState } from './isChildViewport';
import { loadExtraAppConfig } from './loadExtraAppConfig';
import initHangingProtocols from '../protocols/hangingProtocolSetup';
import {
  closeStartupValidation,
  finishStartupCheck,
  beginStartupCheck,
  getStartupValidationErrorTime,
  recordStartupError,
  skipStartupCheck,
  wait,
  waitForStartupCheck,
} from './startupValidation';

export const useApplicationStartup = () => {
  const [isChildWindow] = useState(() => Boolean(getChildViewportState()?.appConfig));
  const [startupResult, setStartupResult] = useState(null);

  useEffect(() => {
    let isActive = true;

    const initialize = async () => {
      try {
        const result = await startApplication();
        if (isActive) setStartupResult(result);
      } catch (error) {
        console.error('Application startup failed:', error);
        recordStartupError();
        if (isActive) {
          setStartupResult({
            appConfig: undefined,
            errorMessages: [error.message || 'Application startup failed'],
            oidcEnabled: false,
            isChildViewport: isChildWindow,
          });
        }
      }
    };

    initialize();
    return () => {
      isActive = false;
    };
  }, [isChildWindow]);

  useEffect(() => {
    if (!startupResult || startupResult.isChildViewport) return;
    finishStartup(startupResult.errorMessages);
  }, [startupResult]);

  return {
    startupResult,
    isChildViewport: startupResult ? startupResult.isChildViewport : isChildWindow,
  };
};

const waitForStartupLayout = async (waitForViewerLayout) => {
  await wait(2500);

  if (!waitForViewerLayout || !window.location.pathname.startsWith('/viewer/')) return;

  await new Promise((resolve) => {
    const timeout = setTimeout(resolve, 2500);
    const interval = setInterval(() => {
      if (document.querySelector('header') && document.querySelector('.ViewerLayout')) {
        clearTimeout(timeout);
        clearInterval(interval);
        resolve();
      }
    }, 100);
  });
};

const validateJwtConfiguration = async (appConfig) => {
  try {
    if (!appConfig) throw new Error('Application configuration is unavailable');
    await jose.importSPKI(appConfig.LOGIN_JWT_PUBLIC_KEY, 'RS256');
    finishStartupCheck('licenseCheck', true);
  } catch (error) {
    console.warn('JWT configuration validation warning:', error);
    finishStartupCheck('licenseCheck', false, true);
  }
};

const validateStartupConfiguration = async (errorMessages) => {
  let appConfig;
  let licenseExpirationDate;
  let isValidLicense = false;
  let licenseCheckFinished = false;
  let licenseConfigCheckFinished = false;

  try {
    appConfig = await getAppConfig();
    if (!appConfig) {
      errorMessages.push('Error getting APP config');
    } else {
      initSentry(appConfig);

      let licenseResult;
      try {
        licenseResult = await verifyLicense(
          appConfig.LICENSE_VERIFY_URL,
          appConfig.LICENSE_TOKEN,
          appConfig.ORDER_NUMBER,
          appConfig.USER,
          appConfig.CLIENT
        );
      } finally {
        finishStartupCheck('licenseVerification', Boolean(licenseResult && licenseResult.isValidLicense));
        licenseCheckFinished = true;
      }

      isValidLicense = Boolean(licenseResult && licenseResult.isValidLicense);
      licenseExpirationDate = licenseResult && licenseResult.licenseExpirationDate;
      if (!isValidLicense) {
        errorMessages.push((licenseResult && licenseResult.verifyLicenseErrorMessage) || 'Error at License Verification');
      }

      if (isValidLicense && appConfig.HAS_LICENSE_CONFIG) {
        try {
          const licenseConfig = await getLicenseConfig(appConfig.LICENSE_CONFIG_URL, appConfig.LICENSE_TOKEN);
          if (!licenseConfig || typeof licenseConfig !== 'object' || Array.isArray(licenseConfig)) {
            throw new Error(t('license.verificationError'));
          }
          appConfig = { ...appConfig, ...licenseConfig };
        } finally {
          licenseConfigCheckFinished = true;
        }
      } else if (appConfig.HAS_LICENSE_CONFIG) {
        skipStartupCheck('securityConfig');
        licenseConfigCheckFinished = true;
      }
    }
  } catch (error) {
    console.error('Application startup validation failed:', error);
    if (!errorMessages.length) errorMessages.push(error.message || 'Application startup failed');
  } finally {
    if (appConfig && !licenseCheckFinished) {
      finishStartupCheck('licenseVerification', false);
    }
    if (appConfig && appConfig.HAS_LICENSE_CONFIG && !licenseConfigCheckFinished) {
      skipStartupCheck('securityConfig');
    }
  }

  return { appConfig, licenseExpirationDate };
};

export const finishStartup = async (errorMessages) => {
  await waitForStartupLayout(errorMessages.length === 0);
  await waitForStartupCheck('licenseCheck');

  const startupErrorAt = getStartupValidationErrorTime();
  if (startupErrorAt) {
    await wait(Math.max(0, startupErrorAt + 30000 - Date.now()));
  } else if (errorMessages.length > 0) {
    await wait(30000);
  }

  closeStartupValidation();
  await wait(350);
};

export const startApplication = async () => {
  const childViewportState = getChildViewportState();
  const isChildViewport = Boolean(childViewportState?.appConfig);

  if (isChildViewport) {
    const appConfig = {
      ...childViewportState.appConfig,
      hangingProtocols: initHangingProtocols(),
    };
    initSentry(appConfig);
    store.dispatch(addConfig(appConfig));

    const { CLOUD_WITH_AUTH, OPEN_ID_CONNECT } = appConfig;
    const oidcEnabled = (OPEN_ID_CONNECT && Object.keys(OPEN_ID_CONNECT)) || CLOUD_WITH_AUTH;
    return { appConfig, errorMessages: [], oidcEnabled, isChildViewport: true };
  }

  const errorMessages = [];
  beginStartupCheck('licenseCheck', 'Token validation');
  let { appConfig, licenseExpirationDate } = await validateStartupConfiguration(errorMessages);
  await validateJwtConfiguration(appConfig);

  try {
    if (appConfig && errorMessages.length === 0) {
      appConfig.licenseExpirationDate = licenseExpirationDate;
      appConfig = loadExtraAppConfig(appConfig);
      appConfig.hangingProtocols = initHangingProtocols();
      store.dispatch(addConfig(appConfig));
    }
  } catch (error) {
    console.error('Application startup failed:', error);
    recordStartupError();
    errorMessages.push(error.message);
  }

  const { CLOUD_WITH_AUTH, OPEN_ID_CONNECT } = appConfig || {};
  const oidcEnabled = (OPEN_ID_CONNECT && Object.keys(OPEN_ID_CONNECT)) || CLOUD_WITH_AUTH;

  return { appConfig, errorMessages, oidcEnabled, isChildViewport: false };
};
